import { PageInfo } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useProductRows';
import { getLocalStorage } from '@shared/core';
import { useMemo } from 'react';

const EXPERIMENT_START_DATE = '2025-08-05T00:00:00Z';
const THINGS_TO_DO_PAGE_SLUG = 'things-to-do';
const THINGS_TO_DO_EDITOR_SEEN = 'THINGS_TO_DO_EDITOR_SEEN';

export const getIsJoyThingsToDoPage = (slug: string, eventDate: string) => {
  // Must be a things-to-do page
  if (slug !== THINGS_TO_DO_PAGE_SLUG) return false;

  // Event must be created after August 5, 2025 date where things to do slug is reserved and users be part of the experiment
  const creationDate = new Date(eventDate);
  const experimentStartDate = new Date(EXPERIMENT_START_DATE);
  if (creationDate < experimentStartDate) return false;

  return true;
};

const EXPERIMENT_V2_START_DATE = new Date('2025-09-15T00:00:00Z');

/**
 * Checks if an event should participate in the Things To Do layout experiment V2.
 * Logic:
 * - Include: Events created after V2 launch (new users)
 * - Include: Events created before V1 launch (very old users who never participated)
 * - Exclude: Events created between V1 and V2 launch (V1 participants)
 *
 * @param pageCreationDate - The event's Firebase creation timestamp
 * @param version - The experiment version ('v1' or 'v2')
 * @returns boolean - true if event should participate in experiment, false otherwise
 */
export const shouldParticipateInExperiment = (pageCreationDate?: string, version: 'v1' | 'v2' = 'v2'): boolean => {
  if (!pageCreationDate) return false;

  const creationDate = new Date(pageCreationDate);

  if (version === 'v1') {
    const experimentStartDate = new Date(EXPERIMENT_START_DATE);
    return creationDate >= experimentStartDate;
  }

  // V2 logic: exclude only V1 participants
  const isAfterV2Launch = creationDate >= EXPERIMENT_V2_START_DATE;
  const isBeforeV1Launch = creationDate < new Date(EXPERIMENT_START_DATE);

  // Include new users (post-V2) and very old users (pre-V1)
  // Exclude V1 participants (between V1 and V2)
  return isAfterV2Launch || isBeforeV1Launch;
};

//util function to check if a given slug is the things to do page slug
export const isThingsToDoSlug = (slug: string) => {
  return slug === THINGS_TO_DO_PAGE_SLUG;
};

export function useThingsToDoPage(eventPages: ReadonlyArray<PageInfo>) {
  // Indicates whether the event has a system-generated Things To Do page from the Joy experiment
  const eventHasSystemCreatedThingsToDoPage = useMemo(() => {
    const createdAt = eventPages.find(item => item.pageSlug === THINGS_TO_DO_PAGE_SLUG)?.createdAt;
    return getIsJoyThingsToDoPage(THINGS_TO_DO_PAGE_SLUG, createdAt?.timestamp || '');
  }, [eventPages]);

  const localStorage = getLocalStorage();

  const isThingsToDoPageEditorSeen = useMemo(() => {
    return localStorage.getItem(THINGS_TO_DO_EDITOR_SEEN) === 'true';
  }, [localStorage]);

  const markThingsToDoPageEditorSeen = () => {
    if (isThingsToDoPageEditorSeen) return;
    localStorage.setItem(THINGS_TO_DO_EDITOR_SEEN, 'true');
  };

  return { eventHasSystemCreatedThingsToDoPage, isThingsToDoPageEditorSeen, markThingsToDoPageEditorSeen, isThingsToDoSlug };
}
